import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import './AgentSetup2FA.css';

function AgentSetup2FA() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Show QR, 2: Verify code
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    generateQRCode();
  }, []);

  const generateQRCode = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/2fa/setup');
      
      if (response.data.success) {
        setQrCode(response.data.qrCode);
        setSecret(response.data.secret);
      }
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerifying(true);

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      setVerifying(false);
      return;
    }

    try {
      const response = await axios.post('/api/2fa/verify-setup', {
        token: verificationCode
      });

      if (response.data.success) {
        setSuccess(true);
        // Redirect to agent dashboard after 2 seconds
        setTimeout(() => {
          navigate('/agent/dashboard');
        }, 2000);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
      setVerificationCode(''); // Clear the input
    } finally {
      setVerifying(false);
    }
  };

  const handleSkipLater = () => {
    // Allow user to skip but they'll be prompted again on next login
    navigate('/agent/dashboard');
  };

  const handleLogout = () => {
    // Clear agent token and redirect to login
    localStorage.removeItem('agentToken');
    localStorage.removeItem('selectedCampaign');
    navigate('/agent/login');
  };

  if (success) {
    return (
      <div className="setup-2fa-page">
        <div className="setup-2fa-container">
          <div className="success-animation">
            <div className="checkmark-circle">
              <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle className="checkmark-circle-bg" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
          </div>
          <h2 style={{ color: '#28a745', marginTop: '20px' }}>2FA Setup Complete!</h2>
          <p>Your account is now secured with two-factor authentication.</p>
          <p style={{ fontSize: '14px', color: '#6c757d' }}>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-2fa-page">
      <div className="setup-2fa-container">
        <div className="setup-2fa-header">
          <h1>🔒 Secure Your Account</h1>
          <p>Set up two-factor authentication for enhanced security</p>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Generating secure QR code...</p>
          </div>
        ) : (
          <>
            {step === 1 ? (
              <div className="qr-code-step">
                <div className="step-indicator">
                  <span className="step-number active">1</span>
                  <span className="step-line"></span>
                  <span className="step-number">2</span>
                </div>

                <h3>Step 1: Scan QR Code</h3>
                <p className="step-description">
                  Use an authenticator app (like Google Authenticator or Authy) to scan this QR code:
                </p>

                <div className="qr-code-container">
                  {qrCode && (
                    <img 
                      src={qrCode} 
                      alt="2FA QR Code" 
                      className="qr-code-image"
                    />
                  )}
                </div>

                <div className="manual-entry">
                  <p className="manual-entry-label">Can't scan? Enter this code manually:</p>
                  <div className="secret-code">
                    <code>{secret}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(secret);
                        alert('Secret copied to clipboard!');
                      }}
                      className="copy-button"
                      title="Copy to clipboard"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="primary-button"
                >
                  Next: Verify Code
                </button>
              </div>
            ) : (
              <div className="verify-code-step">
                <div className="step-indicator">
                  <span className="step-number completed">✓</span>
                  <span className="step-line"></span>
                  <span className="step-number active">2</span>
                </div>

                <h3>Step 2: Verify Your Code</h3>
                <p className="step-description">
                  Enter the 6-digit code from your authenticator app:
                </p>

                <form onSubmit={handleVerify} className="verification-form">
                  <div className="code-input-container">
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => {
                        // Only allow numbers and limit to 6 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setVerificationCode(value);
                        setError('');
                      }}
                      placeholder="000000"
                      className="code-input"
                      maxLength="6"
                      disabled={verifying}
                      autoFocus
                      required
                    />
                  </div>

                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="primary-button"
                    disabled={verifying || verificationCode.length !== 6}
                  >
                    {verifying ? 'Verifying...' : 'Verify & Complete Setup'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="secondary-button"
                    disabled={verifying}
                  >
                    ← Back to QR Code
                  </button>
                </form>
              </div>
            )}

            <div className="setup-2fa-footer">
              <button
                onClick={handleSkipLater}
                className="skip-button"
                disabled={verifying}
              >
                Skip for now (Setup later in settings)
              </button>
              
              <button
                onClick={handleLogout}
                className="logout-button"
                disabled={verifying}
              >
                Logout
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default AgentSetup2FA;
